import { useState, useEffect } from "react";
import { X, Sparkles } from "lucide-react";

// Indian Festival Calendar - auto picks the nearest upcoming festival
const FESTIVALS_2026 = [
  { name: "Makar Sankranti", date: "2026-01-14", emoji: "🪁", offer: "Makar Sankranti Special" },
  { name: "Republic Day", date: "2026-01-26", emoji: "🇮🇳", offer: "Republic Day Sale" },
  { name: "Basant Panchami", date: "2026-02-01", emoji: "🌼", offer: "Basant Panchami Offer" },
  { name: "Valentine's Week", date: "2026-02-14", emoji: "❤️", offer: "Valentine's Week Deal" },
  { name: "Maha Shivratri", date: "2026-02-17", emoji: "🔱", offer: "Maha Shivratri Special" },
  { name: "Holi", date: "2026-03-17", emoji: "🎨", offer: "Holi Dhamaka Offer" },
  { name: "Gudi Padwa / Ugadi", date: "2026-03-29", emoji: "🎉", offer: "New Year Special" },
  { name: "Ram Navami", date: "2026-04-07", emoji: "🙏", offer: "Ram Navami Special" },
  { name: "Baisakhi", date: "2026-04-13", emoji: "🌾", offer: "Baisakhi Offer" },
  { name: "Eid ul-Fitr", date: "2026-04-21", emoji: "🌙", offer: "Eid Special Offer" },
  { name: "Akshaya Tritiya", date: "2026-05-02", emoji: "✨", offer: "Akshaya Tritiya Deal" },
  { name: "Mother's Day", date: "2026-05-10", emoji: "💐", offer: "Mother's Day Special" },
  { name: "Father's Day", date: "2026-06-21", emoji: "👔", offer: "Father's Day Deal" },
  { name: "Eid ul-Adha", date: "2026-06-28", emoji: "🌙", offer: "Eid Special Offer" },
  { name: "Raksha Bandhan", date: "2026-08-12", emoji: "🎀", offer: "Rakhi Special Offer" },
  { name: "Independence Day", date: "2026-08-15", emoji: "🇮🇳", offer: "Independence Day Sale" },
  { name: "Janmashtami", date: "2026-08-22", emoji: "🦚", offer: "Janmashtami Special" },
  { name: "Ganesh Chaturthi", date: "2026-09-01", emoji: "🐘", offer: "Ganesh Chaturthi Offer" },
  { name: "Onam", date: "2026-09-07", emoji: "🌺", offer: "Onam Special Deal" },
  { name: "Navratri", date: "2026-10-10", emoji: "🔥", offer: "Navratri Dhamaka" },
  { name: "Dussehra", date: "2026-10-19", emoji: "🏹", offer: "Dussehra Special" },
  { name: "Karva Chauth", date: "2026-10-23", emoji: "🌕", offer: "Karva Chauth Offer" },
  { name: "Dhanteras", date: "2026-11-06", emoji: "💰", offer: "Dhanteras Special" },
  { name: "Diwali", date: "2026-11-08", emoji: "🪔", offer: "Diwali Mega Sale" },
  { name: "Bhai Dooj", date: "2026-11-10", emoji: "🎊", offer: "Bhai Dooj Offer" },
  { name: "Guru Nanak Jayanti", date: "2026-11-16", emoji: "🙏", offer: "Gurupurab Special" },
  { name: "Christmas", date: "2026-12-25", emoji: "🎄", offer: "Christmas Special" },
  { name: "New Year", date: "2026-12-31", emoji: "🎆", offer: "New Year Mega Deal" },
];

const getUpcomingFestival = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (const fest of FESTIVALS_2026) {
    const festDate = new Date(fest.date);
    const diffDays = Math.ceil((festDate - today) / (1000 * 60 * 60 * 24));
    // Show banner 7 days before and 1 day after the festival
    if (diffDays >= -1 && diffDays <= 7) {
      return { ...fest, daysLeft: diffDays };
    }
  }
  // If no festival nearby, find the next one
  for (const fest of FESTIVALS_2026) {
    const festDate = new Date(fest.date);
    if (festDate >= today) {
      const diffDays = Math.ceil((festDate - today) / (1000 * 60 * 60 * 24));
      if (diffDays <= 15) {
        return { ...fest, daysLeft: diffDays };
      }
      break;
    }
  }
  return null;
};

const FestivalBanner = () => {
  const [festival, setFestival] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("festivalBannerDismissed")) return;
    const f = getUpcomingFestival();
    if (f) setFestival(f);
  }, []);

  if (!festival || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("festivalBannerDismissed", "true");
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    handleDismiss();
  };

  const isNational = ["Independence Day", "Republic Day"].includes(festival.name);
  const bg = isNational
    ? "linear-gradient(100deg, #FF9933 0%, #FF9933 20%, #fff7ed 38%, #ffffff 50%, #f0fdf4 62%, #138808 82%, #0a6b06 100%)"
    : "linear-gradient(100deg, #7c2d12 0%, #dc2626 30%, #f97316 55%, #fbbf24 80%, #f59e0b 100%)";
  const dark = isNational;

  return (
    <div className="fixed top-[64px] md:top-[72px] left-0 right-0 z-[140] animate-slideDown" data-testid="festival-banner">
      <div className="relative overflow-hidden py-2.5 px-3 sm:px-4 shadow-2xl" style={{ background: bg }}>
        <div className="fest-shine absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 relative z-10">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl flex-shrink-0 fest-bounce">{festival.emoji}</span>
            <div className="min-w-0">
              <p className={`text-[13px] sm:text-[15px] font-black leading-tight truncate ${dark ? "text-gray-900" : "text-white"}`}>
                {festival.offer} — <span className={dark ? "text-red-700" : "text-yellow-300"}>FLAT 20% OFF</span> on Marketplace Onboarding
                {festival.daysLeft > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-black align-middle ${dark ? "bg-[#000080] text-white" : "bg-white/25 text-white"}`}>
                    {festival.daysLeft === 1 ? "TOMORROW" : `${festival.daysLeft} DAYS LEFT`}
                  </span>
                )}
              </p>
              <p className={`hidden md:block text-[11px] font-semibold truncate ${dark ? "text-gray-700" : "text-white/85"}`}>
                Myntra, Amazon & Flipkart festive sales incoming — get your catalog approved & ready to scale before the rush.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={scrollToContact}
              data-testid="festival-banner-cta"
              className="flex items-center gap-1.5 px-4 sm:px-5 py-2 bg-white text-gray-900 rounded-full text-[11px] sm:text-xs font-black uppercase tracking-wider shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <Sparkles size={13} className="text-orange-500" />
              <span className="hidden sm:inline">Start Onboarding</span>
              <span className="sm:hidden">Claim</span>
              <span aria-hidden>→</span>
            </button>
            <button onClick={handleDismiss} data-testid="festival-banner-close" aria-label="Close banner" className={`p-1.5 rounded-full transition-colors ${dark ? "text-gray-700 hover:bg-black/10" : "text-white/80 hover:bg-white/15"}`}>
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        .animate-slideDown { animation: slideDown 0.4s ease-out; }
        .fest-shine::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -30%;
          width: 22%;
          height: 200%;
          background: linear-gradient(105deg, transparent, rgba(255,255,255,0.45), transparent);
          transform: skewX(-20deg);
          animation: festShine 4.5s ease-in-out infinite;
        }
        @keyframes festShine { 0%, 55% { left: -30%; } 100% { left: 130%; } }
        .fest-bounce { animation: festBounce 2.2s ease-in-out infinite; display: inline-block; }
        @keyframes festBounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-4px) rotate(6deg); } }
      `}</style>
    </div>
  );
};

export default FestivalBanner;
